import React from 'react';
import { Radio, GitBranch, CalendarClock, Search, Target, Award } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface SolutionCard {
  id: string;
  icon: LucideIcon;
  title: string;
  body: string;
  accent: string;
  accentBg: string;
  accentBorder: string;
  accentText: string;
}

const solutions: SolutionCard[] = [
  {
    id: 'torre-controle',
    icon: Radio,
    title: 'Torre de Controle de Performance Operacional',
    body:
      'Cockpit em tempo real que consolida o status da operação. Identifique gargalos, antecipe atrasos e visualize a ocupação das equipes — decisões estratégicas sem depender de planilhas.',
    accent: '#2563eb',
    accentBg: 'bg-blue-50',
    accentBorder: 'border-blue-100',
    accentText: 'text-blue-600',
  },
  {
    id: 'modelagem-visual',
    icon: GitBranch,
    title: 'Modelagem Visual e Otimização Automática',
    body:
      'Desenhe fluxos produtivos ou administrativos com jornada visual guiada ou importação de dados. A plataforma projeta fluxos mais enxutos e reduz o Lead Time das suas demandas.',
    accent: '#4f46e5',
    accentBg: 'bg-indigo-50',
    accentBorder: 'border-indigo-100',
    accentText: 'text-indigo-600',
  },
  {
    id: 'sustentacao-rotina',
    icon: CalendarClock,
    title: 'Sustentação de Processos e Gestão da Rotina',
    body:
      'Cronogramas visuais para rotinas críticas da liderança, mudando o gestor de reativo para preventivo. Proteja melhorias contra retrocesso e perda de eficiência operacional.',
    accent: '#d97706',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-100',
    accentText: 'text-amber-700',
  },
  {
    id: 'resolucao-falhas',
    icon: Search,
    title: 'Resolução Estruturada de Falhas Crônicas',
    body:
      'Jornada guiada para eliminar a causa raiz de anomalias operacionais. Planos de ação vinculados ao fluxo de trabalho, com monitoramento de prazos e travas inteligentes.',
    accent: '#7c3aed',
    accentBg: 'bg-violet-50',
    accentBorder: 'border-violet-100',
    accentText: 'text-violet-600',
  },
  {
    id: 'desdobramento-estrategico',
    icon: Target,
    title: 'Desdobramento Estratégico Conectado à Operação',
    body:
      'Conecte metas da diretoria às tarefas diárias da operação com árvore visual de progresso. Acompanhe iniciativas em tempo real e alinhe o foco entre todas as camadas.',
    accent: '#059669',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-100',
    accentText: 'text-emerald-600',
  },
  {
    id: 'score-governanca',
    icon: Award,
    title: 'Score de Performance e Governança Corporativa',
    body:
      'Algoritmo proprietário que gera uma nota única de aderência à rotina por colaborador. Permissões por nível de cargo e segurança em conformidade com a legislação de dados.',
    accent: '#0891b2',
    accentBg: 'bg-cyan-50',
    accentBorder: 'border-cyan-100',
    accentText: 'text-cyan-600',
  },
];

function SolutionCardView({ card, index }: { card: SolutionCard; index: number }) {
  const Icon = card.icon;
  const num = String(index).padStart(2, '0');

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300/80 hover:shadow-lg">
      <div className="h-1 shrink-0" style={{ backgroundColor: card.accent }} />

      <div className="relative flex h-full flex-col p-6 md:p-8">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div className={`rounded-xl border p-3 shadow-sm ${card.accentBg} ${card.accentBorder}`}>
            <Icon size={22} className={card.accentText} strokeWidth={1.75} />
          </div>
          <span
            className="select-none text-4xl font-black leading-none tracking-tighter text-slate-100 transition-colors duration-300 group-hover:text-slate-200/90"
            aria-hidden
          >
            {num}
          </span>
        </div>

        <h3 className="mb-3 text-xl font-bold leading-snug tracking-tight text-slate-900 md:text-2xl">
          {card.title}
        </h3>

        <p className="text-sm leading-relaxed text-slate-600 md:text-base">
          {card.body}
        </p>
      </div>
    </article>
  );
}

const highlights = ['Visibilidade executiva', 'Execução disciplinada', 'Governança integrada'];

export function SiteSolutionsSection() {
  return (
    <section id="solutions" className="bg-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="relative mx-auto mb-10 max-w-4xl text-center">
          <div
            className="pointer-events-none absolute inset-0 -z-10 rounded-3xl opacity-60"
            style={{
              background:
                'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(59,130,246,0.07) 0%, transparent 70%)',
            }}
          />

          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-700 md:text-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)]" />
            Plataforma de Soluções
          </span>

          <h2 className="mb-5 text-3xl font-extrabold leading-[1.12] tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
            Da Operação à Estratégia
            <span className="mt-2 block text-blue-600">
              Uma Plataforma que Resolve Dores Reais.
            </span>
          </h2>

          <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Seis capacidades integradas para transformar a gestão de processos da sua empresa — com
            visibilidade executiva, execução disciplinada e governança corporativa em um único ecossistema.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {highlights.map((item) => (
              <span
                key={item}
                className="rounded-full border border-slate-200/80 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm md:text-sm"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {solutions.map((card, index) => (
            <SolutionCardView key={card.id} card={card} index={index + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
